// Independently vendored per product. Only the server grants workspace access.
export const PRODUCT = "LEADCAT_CRM";
const key = "leadcat.workspace.context.v1";
const nativeSignIn = "/react/login";
export type Workspace = {
  context_handle: string;
  csrf_token: string;
  context: {
    product_code: string;
    source_workspace_id: string;
    source_parent_id: string | null;
    leadcat_user_id: string;
    mapping_id: string;
    [key: string]: unknown;
  };
};
let enabled = false;
let validation = 0;
export function managedWorkspace(): Workspace | null {
  if (typeof sessionStorage === "undefined") return null;
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  let value: Workspace;
  try {
    value = JSON.parse(raw);
  } catch {
    throw new Error("Choose your business again.");
  }
  if (
    !value ||
    !/^[A-Za-z0-9_-]{43,128}$/.test(value.context_handle) ||
    !value.context ||
    value.context.product_code !== PRODUCT ||
    typeof value.csrf_token !== "string" ||
    !value.csrf_token ||
    value.csrf_token.length > 128 ||
    typeof value.context.source_workspace_id !== "string" ||
    !value.context.source_workspace_id ||
    typeof value.context.mapping_id !== "string" ||
    !value.context.mapping_id ||
    typeof value.context.leadcat_user_id !== "string" ||
    !value.context.leadcat_user_id
  ) {
    throw new Error("Choose your business again.");
  }
  return value;
}
export function clearManagedWorkspace() {
  validation++;
  enabled = false;
  sessionStorage.removeItem(key);
  sessionStorage.removeItem("leadcat.workspace.pending.v1");
}
export async function initializeManaged(): Promise<boolean> {
  const ticket = ++validation;
  enabled = false;
  const current = managedWorkspace();
  let response: Response;
  try {
    response = await fetch("/platform/entry-config", {
      credentials: "same-origin",
      redirect: "error",
      cache: "no-store",
    });
  } catch {
    if (current) throw new Error("Business sign-in is unavailable.");
    return false;
  }
  if (!response.ok) {
    if (current) throw new Error("Business sign-in is unavailable.");
    return false;
  }
  const config = await response.json();
  const active = config.enabled === true && config.product_code === PRODUCT;
  if (!current && config.managed_session_present === true)
    throw new Error("Choose a business or use product sign-in.");
  if (!active) {
    if (current) throw new Error("Business sign-in is unavailable.");
    return false;
  }
  if (!current) return true;
  const person = await fetch("/platform/workspaces/person", {
    credentials: "same-origin",
    redirect: "error",
    cache: "no-store",
  });
  if (!person.ok) throw new Error("Sign in again to continue.");
  const human = await person.json();
  if (
    human.person_id !== current.context.leadcat_user_id ||
    typeof human.csrf_token !== "string" ||
    !human.csrf_token
  ) {
    throw new Error("Sign in again for this business.");
  }
  const check = await fetch("/platform/workspaces/context", {
    credentials: "same-origin",
    redirect: "error",
    cache: "no-store",
    headers: { "X-LeadCat-Context": current.context_handle },
  });
  if (!check.ok)
    throw new Error("This business is unavailable or needs access review.");
  const value = await check.json();
  if (
    value.context?.mapping_id !== current.context.mapping_id ||
    value.context?.product_code !== PRODUCT ||
    value.context?.leadcat_user_id !== human.person_id ||
    value.context?.source_workspace_id !==
      current.context.source_workspace_id ||
    value.context?.source_parent_id !== current.context.source_parent_id
  )
    throw new Error("Choose your business again.");
  if (
    ticket !== validation ||
    managedWorkspace()?.context_handle !== current.context_handle
  )
    throw new Error("Your business changed. Reload to continue.");
  sessionStorage.setItem(
    key,
    JSON.stringify({
      ...current,
      context: value.context,
      csrf_token: human.csrf_token,
    }),
  );
  enabled = true;
  return true;
}
function assertCurrent(handle: string) {
  if (!enabled || managedWorkspace()?.context_handle !== handle)
    throw new Error("Your business changed. Reload to continue.");
}
function protectResponse(response: Response, handle: string): Response {
  // Fence after body decoding too: a switch can occur after headers arrive.
  for (const name of [
    "text",
    "json",
    "blob",
    "arrayBuffer",
    "formData",
  ] as const) {
    const read = response[name].bind(response);
    Object.defineProperty(response, name, {
      value: async () => {
        assertCurrent(handle);
        const data = await read();
        assertCurrent(handle);
        return data;
      },
    });
  }
  const clone = response.clone.bind(response);
  Object.defineProperty(response, "clone", {
    value: () => {
      assertCurrent(handle);
      return protectResponse(clone(), handle);
    },
  });
  return response;
}
export async function productFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const current = managedWorkspace();
  if (!current) {
    if (enabled) throw new Error("Choose your business again.");
    return fetch(path, init);
  }
  if (!enabled) throw new Error("Business sign-in is unavailable.");
  const url = new URL(path, location.origin);
  if (url.origin !== location.origin || !url.pathname.startsWith("/api/"))
    throw new Error("Unsupported business request.");
  const headers = new Headers(init.headers);
  headers.delete("Authorization");
  headers.set("X-LeadCat-Context", current.context_handle);
  if (
    !["GET", "HEAD", "OPTIONS"].includes((init.method || "GET").toUpperCase())
  )
    headers.set("X-CSRF-Token", current.csrf_token);
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: "same-origin",
    redirect: "error",
  });
  assertCurrent(current.context_handle);
  return protectResponse(response, current.context_handle);
}
async function leaveBrowserSession() {
  let current: Workspace | null = null;
  try {
    current = managedWorkspace();
  } catch {
    /* Recovery also handles corrupt tab storage. */
  }
  if (current) {
    try {
      await fetch("/platform/workspaces/logout", {
        method: "POST",
        credentials: "same-origin",
        redirect: "error",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": current.csrf_token,
        },
        body: "{}",
      });
    } catch {
      /* Clearing the browser cookie below remains mandatory. */
    }
  }
  const response = await fetch("/platform/leave-managed", {
    method: "POST",
    credentials: "same-origin",
    redirect: "error",
    headers: { "Content-Type": "application/json", "X-LeadCat-Leave": "1" },
    body: "{}",
  });
  if (!response.ok)
    throw new Error("Unable to return to product sign-in. Please try again.");
  clearManagedWorkspace();
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel("leadcat.identity");
    channel.postMessage("changed");
    channel.close();
  }
}
export async function useProductSignIn() {
  await leaveBrowserSession();
  location.assign(nativeSignIn);
}
export async function managedLogout() {
  await leaveBrowserSession();
  location.assign(nativeSignIn);
}
